import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';

import CountryFlag from '@/common/country-flag';
import LanguagesEnum, { AceptedLanguagesBlogEnum, findBySlug, getBlogLocalePrefix } from '@/enums/languages-enum';

const getLangCode = (slug?: string) => (String(slug ?? 'en').split('-')[0] ?? 'en').toUpperCase();
const getClassName = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

const triggerClassName = 'border-[#f4f6f7] border-opacity-20 lang inline-flex cursor-pointer items-center gap-1.5 rounded-lg border bg-white/[0.05] px-2.5 py-1 font-mono text-xs tracking-[0.1em] text-[var(--np-fg-dim)] transition-[background,border-color,color] duration-200 hover:border-white/20 hover:bg-white/[0.08] hover:text-[var(--np-fg)]';
const triggerOpenClassName = 'is-open border-white/20 bg-white/[0.08] text-[var(--np-fg)]';
const menuItemClassName = 'inline-flex min-h-10 min-w-0 cursor-pointer items-center justify-start gap-2 rounded-md border-0 bg-transparent px-2 py-[7px] font-mono text-[11px] font-semibold tracking-[0.1em] text-[var(--np-fg-dim)] transition-[background,color] duration-150 hover:bg-white/[0.05] hover:text-[var(--np-fg)]';
const menuItemActiveClassName = 'bg-[rgba(156,255,46,0.10)] text-[var(--np-green)] ring-1 ring-[rgba(156,255,46,0.28)]';
const dropdownMenuClassName = 'lang-menu absolute end-0 top-[calc(100%+10px)] z-[100] grid max-h-[min(70vh,560px)] w-[min(640px,calc(100vw-32px))] animate-[langMenuIn_.18s_var(--np-ease-out)] grid-cols-[repeat(4,minmax(104px,1fr))] gap-1 overflow-auto rounded-[10px] border border-white/[0.08] bg-[rgba(14,17,20,0.96)] p-2 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.75),0_0_0_1px_rgba(255,255,255,0.02)] backdrop-blur-2xl max-[640px]:grid-cols-2';
const listMenuClassName = 'lang-menu grid w-full grid-cols-[repeat(auto-fit,minmax(104px,1fr))] gap-1 rounded-[10px] border border-white/[0.08] bg-[rgba(14,17,20,0.96)] p-2';

const languageCodeBySlug: Record<string, string> = {
  en: 'en',
  fr: 'fr',
  jp: 'ja',
  kr: 'ko',
  pt: 'pt',
  ru: 'ru',
  sa: 'ar',
  tr: 'tr',
  cn: 'zh',
  es: 'es',
};

const localeAliases: Record<string, string> = {
  ar: 'sa',
  ja: 'jp',
  ko: 'kr',
  zh: 'cn',
};

const localizedLanguageNames: Record<string, Record<string, string>> = {
  en: { en: 'English', fr: 'French', ja: 'Japanese', ko: 'Korean', pt: 'Portuguese', ru: 'Russian', ar: 'Arabic', tr: 'Turkish', zh: 'Chinese', es: 'Spanish' },
  pt: { en: 'Inglês', fr: 'Francês', ja: 'Japonês', ko: 'Coreano', pt: 'Português', ru: 'Russo', ar: 'Árabe', tr: 'Turco', zh: 'Chinês', es: 'Espanhol' },
  es: { en: 'Inglés', fr: 'Francés', ja: 'Japonés', ko: 'Coreano', pt: 'Portugués', ru: 'Ruso', ar: 'Árabe', tr: 'Turco', zh: 'Chino', es: 'Español' },
  fr: { en: 'Anglais', fr: 'Français', ja: 'Japonais', ko: 'Coréen', pt: 'Portugais', ru: 'Russe', ar: 'Arabe', tr: 'Turc', zh: 'Chinois', es: 'Espagnol' },
  ru: { en: 'английский', fr: 'французский', ja: 'японский', ko: 'корейский', pt: 'португальский', ru: 'русский', ar: 'арабский', tr: 'турецкий', zh: 'китайский', es: 'испанский' },
  tr: { en: 'İngilizce', fr: 'Fransızca', ja: 'Japonca', ko: 'Korece', pt: 'Portekizce', ru: 'Rusça', ar: 'Arapça', tr: 'Türkçe', zh: 'Çince', es: 'İspanyolca' },
  jp: { en: '英語', fr: 'フランス語', ja: '日本語', ko: '韓国語', pt: 'ポルトガル語', ru: 'ロシア語', ar: 'アラビア語', tr: 'トルコ語', zh: '中国語', es: 'スペイン語' },
  kr: { en: '영어', fr: '프랑스어', ja: '일본어', ko: '한국어', pt: '포르투갈어', ru: '러시아어', ar: '아랍어', tr: '터키어', zh: '중국어', es: '스페인어' },
  sa: { en: 'الإنجليزية', fr: 'الفرنسية', ja: 'اليابانية', ko: 'الكورية', pt: 'البرتغالية', ru: 'الروسية', ar: 'العربية', tr: 'التركية', zh: 'الصينية', es: 'الإسبانية' },
  cn: { en: '英语', fr: '法语', ja: '日语', ko: '韩语', pt: '葡萄牙语', ru: '俄语', ar: '阿拉伯语', tr: '土耳其语', zh: '中文', es: '西班牙语' },
};

const getLocalizedLanguageName = (locale: string | undefined, slug: string, fallback: string) => {
  const baseLocale = String(locale ?? 'en').split('-')[0] ?? 'en';
  const localeKey = localeAliases[baseLocale] ?? baseLocale;
  const languageCode = languageCodeBySlug[slug] ?? slug;

  const namesForLocale = localizedLanguageNames[localeKey] ?? localizedLanguageNames.en;

  return namesForLocale?.[languageCode] ?? localizedLanguageNames.en?.[languageCode] ?? fallback;
};

type LanguageSwitcherProps = {
  mode?: 'dropdown' | 'list';
};

export default function LanguageSwitcher({ mode = 'dropdown' }: LanguageSwitcherProps) {
  const { i18n } = useTranslation('common');
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);

  const isBlogPage = router.pathname.includes('/blog');
  const languageOptions = useMemo(() => Object.values(isBlogPage ? AceptedLanguagesBlogEnum : LanguagesEnum), [isBlogPage]);
  const selectedLanguage = findBySlug(i18n.language) ?? LanguagesEnum.ENGLISH;
  const currentLocale = i18n.resolvedLanguage ?? i18n.language;

  const handleChangeLanguage = useCallback(
    (lang: string) => {
      setMenuOpen(false);

      // Next redirects a full load of `/` by Accept-Language unless this cookie names the locale — without it, English (the only unprefixed locale) bounces back to /pt.
      // The middleware also honours it on every unprefixed URL (SCRUM-2127), so it is written even for the language already on screen: on a page that ignores the cookie (the blog, `/en`) that click is how a visitor fixes a stale one.
      document.cookie = `NEXT_LOCALE=${lang}; path=/; max-age=31536000; SameSite=Lax`;

      if (lang === i18n.language) {
        return;
      }

      if (isBlogPage) {
        window.location.assign(`${getBlogLocalePrefix(lang)}/blog`);
        return;
      }

      router.push(
        {
          pathname: router.pathname,
          query: router.query,
        },
        undefined,
        { locale: lang }
      );
    },
    [i18n.language, isBlogPage, router]
  );

  const renderLanguageOption = (lang: (typeof languageOptions)[number]) => {
    const isSelected = lang.slug === selectedLanguage.slug;
    const label = getLocalizedLanguageName(currentLocale, lang.slug, lang.name);

    return (
      <button key={`country-${lang.slug}`} type='button' className={getClassName(menuItemClassName, isSelected && menuItemActiveClassName)} onClick={() => handleChangeLanguage(lang.slug)} title={lang.title} aria-current={isSelected ? 'true' : undefined} aria-pressed={isSelected} aria-label={`${getLangCode(lang.slug)} ${label}${isSelected ? ', selected' : ''}`}>
        <span className='inline-block h-[13px] w-[18px] shrink-0 overflow-hidden rounded-[2px] leading-none shadow-[inset_0_0_0_1px_rgba(255,255,255,0.10)]'>
          <CountryFlag country={lang.slug} alt={lang.title} className='size-full object-cover' />
        </span>

        <span className='flex min-w-0 flex-1 flex-col gap-[3px] text-start text-[#f4f6f785]'>
          <span className={getClassName('whitespace-nowrap text-[18px] font-normal leading-none', isSelected && 'text-[var(--np-green)]')}>{getLangCode(lang.slug)}</span>
          <span className={getClassName('max-w-[74px] truncate font-sans text-[9px] font-semibold leading-none tracking-normal text-[var(--np-fg-mute)]', isSelected && 'text-[rgba(156,255,46,0.72)]')}>{label}</span>
        </span>

        {isSelected && (
          <span className='ms-auto inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-[var(--np-green)] text-[#f4f6f785]'>
            <svg width='10' height='10' viewBox='0 0 10 10' fill='none' aria-hidden='true'>
              <path d='M2 5.1 4.1 7 8 3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </span>
        )}
      </button>
    );
  };

  const languageList = <div className={mode === 'list' ? listMenuClassName : dropdownMenuClassName}>{languageOptions.map(renderLanguageOption)}</div>;

  if (mode === 'list') {
    return languageList;
  }

  return (
    <div className='lang-wrap relative'>
      {/* The accessible name starts with the visible code ("PT"): WCAG 2.5.3, Lighthouse `label-content-name-mismatch`. */}
      <button type='button' className={getClassName(triggerClassName, menuOpen && triggerOpenClassName)} onClick={() => setMenuOpen((open) => !open)} aria-haspopup='true' aria-expanded={menuOpen} aria-label={`${getLangCode(selectedLanguage.slug)} — Change language, current ${getLocalizedLanguageName(currentLocale, selectedLanguage.slug, selectedLanguage.name)}`}>
        <span className='inline-block h-[13px] w-[18px] shrink-0 overflow-hidden rounded-[2px] leading-none shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]'>
          <CountryFlag country={selectedLanguage.slug} alt={selectedLanguage.title} className='size-full object-cover' />
        </span>

        <span className='text-[18px] text-[#f4f6f7] text-opacity-70'>{getLangCode(selectedLanguage.slug)}</span>

        <svg className={getClassName('lang-chev text-[#f4f6f7] text-opacity-70 transition-transform duration-200', menuOpen && 'rotate-180')} width='10' height='10' viewBox='0 0 10 10' fill='none'>
          <path d='M2 3.5 L5 6.5 L8 3.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      </button>

      {menuOpen && languageList}
    </div>
  );
}
